import * as React from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Box,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
} from "@mui/material";
import CustomizedDataGrid from "../../Components/CustomizedDataGrid";
import axios from '../../../Configs/axiosInstance';
import { MAIN_URL } from "../../../Configs/Urls";
import { colorForName, initialsFor } from "../../PatientsPage/Components/avatarUtils";
import { getStoredPageSize, setStoredPageSize } from "../../../Configs/paginationStorage";

import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";

export default function MainGrid() {
  const navigate = useNavigate();
  const [dietPlans, setDietPlans] = React.useState([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: getStoredPageSize("patientDietPlans"),
  });
  const handlePaginationModelChange = (model) => {
    setPaginationModel(model);
    setStoredPageSize("patientDietPlans", model.pageSize);
  };
  const [searchTerm, setSearchTerm] = useState("");

  const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false);
  const [Deleteloading, setDeleteLoading] = React.useState(false);
  const [openSnackbar, setOpenSnackbar] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState("");
  const [snackbarSeverity, setSnackbarSeverity] = React.useState("success");

  const [PatientId, setPatientId] = React.useState(null);
  const [PlanId, setPlanId] = React.useState(null);

  // Fetch exactly one page from the backend — either the plain listing or
  // the search endpoint, depending on whether there's a search term. Both
  // return the same { data, meta } shape.
  const fetchDietPlans = async () => {
    try {
      setLoading(true);
      setError(null);

      const trimmedSearch = searchTerm.trim();
      const endpoint = trimmedSearch ? "patientdietplans/search" : "patientdietplans";
      const params = {
        page: paginationModel.page + 1, // backend pages are 1-indexed
        per_page: paginationModel.pageSize,
        ...(trimmedSearch ? { query: trimmedSearch } : {}),
      };

      const response = await axios.get(`${MAIN_URL}${endpoint}`, { params });

      setDietPlans(response.data.data);
      setRowCount(response.data.meta.total);
    } catch (error) {
      console.error("Error fetching diet plans:", error);
      setError("Failed to fetch diet plans. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Refetch whenever the page/page-size changes, or 450ms after the admin
  // stops typing in the search box.
  React.useEffect(() => {
    const handler = setTimeout(fetchDietPlans, searchTerm ? 450 : 0);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel, searchTerm]);

  // A new search resets to page 1 — otherwise the admin can land on a page
  // that no longer exists for the narrower result set.
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  };

  const handleEdit = (row) => {
    navigate(`/patient-diet/${row.id}/edit`);
  };

  const handleView = (row) => {
    navigate(`/patient-diet/${row.id}/view`);
  };

  const handleDelete = (row) => {
    setPatientId(row.patientId);
    setPlanId(row.id);
    setOpenDeleteDialog(true);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const confirmDelete = async () => {
    setDeleteLoading(true);
    try {
      await axios.delete(`${MAIN_URL}patientdietplans/${PlanId}`);
      setSnackbarMessage("Diet plan deleted successfully");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
      fetchDietPlans(); // re-fetch so pagination/total stays accurate
    } catch (error) {
      console.error("Error deleting diet plan:", error);
      setSnackbarMessage(
        error?.response?.data?.message
          ? error?.response.data.message
          : "Error deleting diet plan. Please try again."
      );
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setOpenDeleteDialog(false);
      setPatientId(null);
      setPlanId(null);
      setDeleteLoading(false);
    }
  };

  const columns = [
    {
      field: "patientName",
      headerName: "Name",
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, height: "100%" }}>
          <Avatar sx={{ width: 32, height: 32, fontSize: "0.8rem", bgcolor: colorForName(params.value) }}>
            {initialsFor(params.value)}
          </Avatar>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "weekDateStart",
      headerName: "Plan Start Date",
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>{params.value}</Box>
      ),
    },
    {
      field: "weekDateEnd",
      headerName: "Plan End Date",
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>{params.value}</Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, height: "100%" }}>
          <Tooltip title="View">
            <IconButton
              size="small"
              color="info"
              onClick={(event) => {
                event.stopPropagation();
                handleView(params.row);
              }}
            >
              <VisibilityRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              color="primary"
              onClick={(event) => {
                event.stopPropagation();
                handleEdit(params.row);
              }}
            >
              <EditRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={(event) => {
                event.stopPropagation();
                handleDelete(params.row);
              }}
            >
              <DeleteRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%", maxWidth: { sm: "100%", md: "1700px" } }}>
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, sm: 2.5 },
          mb: 2.5,
          borderRadius: 3,
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Diet Plans
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {rowCount} {rowCount === 1 ? "plan" : "plans"} in total
          </Typography>
        </Box>

        <TextField
          size="small"
          sx={{ width: { xs: "100%", sm: 320 } }}
          variant="outlined"
          placeholder="Search by patient name"
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {error && (
        <Typography component="h2" variant="h6" color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Paper variant="outlined" sx={{ borderRadius: 3, borderColor: "divider", overflow: "hidden" }}>
        <CustomizedDataGrid
          rows={dietPlans}
          columns={columns}
          loading={loading}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[10, 25, 50]}
        />
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => {
          setOpenDeleteDialog(false);
          setPatientId(null);
          setPlanId(null);
        }}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this diet plan?</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenDeleteDialog(false);
              setPatientId(null);
              setPlanId(null);
            }}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            disabled={Deleteloading}
            variant="contained"
            color="error"
            size="small"
          >
            {Deleteloading ? "Deleting ..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} variant="filled" sx={{ fontWeight: 600 }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
